import { computed, ref } from 'vue'

import type { QueueViewMode } from '@/components/designSystem/QueueViews/types'
import { readQueueViewPreference } from '@/components/designSystem/QueueViews/useQueueViewPreference'
import {
  CONTAINER_SLA_FILTER_STATES,
  CONTAINER_STAGES,
  DS_ATTENDANCE_CLOSED_WINDOW_DAYS,
  DS_ATTENDANCE_QUEUE_SCREEN_KEY,
  OPEN_CONTAINER_STAGES
} from '@/constants/ds-attendance-sla.constants'
import { DSAttendanceStatus } from '@/constants/ds-attendance.constants'
import type {
  ContainerSlaFilterState,
  ContainerStage,
  DsAttendanceGroupBy
} from '@/constants/ds-attendance-sla.constants'
import type {
  DsAttendanceQueueBaseParams,
  ListContainersByCompanyParams
} from '@/types/ds-attendance.types'

/**
 * Recorte SLA das abas Todos/Folha/Atividade (spec 2026-10-01 §7.2/§7.5), no padrão de
 * `useActivityRequestFilters`: estado ⇄ URL tolerante e UMA função que compõe os parâmetros
 * (lista, Kanban, contadores, export e "próximo" passam por ela). Os filtros de hoje continuam
 * no `Index.vue` — este composable só acrescenta o que é de SLA.
 */

// Re-exportados para quem consome o composable não precisar saber onde moram (Task 3).
export type { DsAttendanceGroupBy, DsAttendanceQueueBaseParams }

export interface DsAttendanceQueueFilters {
  view: QueueViewMode
  groupBy: DsAttendanceGroupBy
  slaStates: ContainerSlaFilterState[]
  stages: ContainerStage[]
  onlyOverdue: boolean
}

export const DEFAULT_DS_ATTENDANCE_QUEUE_FILTERS: Readonly<DsAttendanceQueueFilters> =
  Object.freeze({
    view: 'kanban',
    groupBy: 'estado',
    slaStates: [],
    stages: [],
    onlyOverdue: false
  }) as Readonly<DsAttendanceQueueFilters>

export const DS_ATTENDANCE_QUEUE_URL_KEYS = [
  'visao',
  'agrupar',
  'slaState',
  'stage',
  'overdue'
] as const

export interface QueueParamsContext {
  nowMs: number
  /** Status ou "Data plantão" escolhidos: a pessoa pediu histórico, sem o corte de 7 dias (G2-D8). */
  hasExplicitHistory: boolean
}

const VIEW_URL_VALUE: Record<QueueViewMode, string> = {
  list: 'lista',
  table: 'tabela',
  kanban: 'kanban'
}
const VIEW_ALIASES: Record<string, QueueViewMode> = {
  lista: 'list',
  list: 'list',
  tabela: 'table',
  table: 'table',
  kanban: 'kanban'
}

/** Primeiro valor não vazio (`?visao=&visao=lista` → `lista`). */
const first = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = first(item)
      if (text !== null) return text
    }
    return null
  }
  return value === undefined || value === null || value === '' ? null : String(value)
}

const csv = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.flatMap(csv)
  const text = first(value)
  return text
    ? text
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
    : []
}

const subsetOf = <T extends string>(allowed: readonly T[], value: unknown): T[] =>
  [...new Set(csv(value))].filter((item): item is T =>
    (allowed as readonly string[]).includes(item)
  )

export function parseQueueView(value: unknown): QueueViewMode | null {
  const text = first(value)?.toLowerCase()
  // `Object.hasOwn`: `?visao=constructor`/`__proto__` não podem resolver chaves herdadas.
  return text && Object.hasOwn(VIEW_ALIASES, text) ? VIEW_ALIASES[text] : null
}

export function parseGroupBy(value: unknown): DsAttendanceGroupBy {
  return first(value) === 'etapa' ? 'etapa' : 'estado'
}

export function dsAttendanceQueueFiltersFromUrl(
  query: Record<string, unknown>,
  fallbackView: QueueViewMode = 'kanban'
): DsAttendanceQueueFilters {
  return {
    view: parseQueueView(query.visao) ?? fallbackView,
    groupBy: parseGroupBy(query.agrupar),
    slaStates: subsetOf(CONTAINER_SLA_FILTER_STATES, query.slaState),
    stages: subsetOf(CONTAINER_STAGES, query.stage),
    onlyOverdue: first(query.overdue) === 'true'
  }
}

/** `visao` sempre (o link compartilhado precisa carregá-la); `agrupar` só no Kanban. */
export function dsAttendanceQueueFiltersToUrl(
  filters: DsAttendanceQueueFilters
): Record<string, string> {
  const query: Record<string, string> = { visao: VIEW_URL_VALUE[filters.view] }
  if (filters.view === 'kanban') query.agrupar = filters.groupBy
  if (filters.slaStates.length) query.slaState = filters.slaStates.join(',')
  if (filters.stages.length) query.stage = filters.stages.join(',')
  if (filters.onlyOverdue) query.overdue = 'true'
  return query
}

/** O que vale de fato: no K1 o Estado do prazo é a coluna; no K2, a Etapa (§7.6). */
export function effectiveQueueFilters(
  filters: DsAttendanceQueueFilters
): Pick<DsAttendanceQueueFilters, 'slaStates' | 'stages' | 'onlyOverdue'> {
  const isK1 = filters.view === 'kanban' && filters.groupBy === 'estado'
  const isK2 = filters.view === 'kanban' && filters.groupBy === 'etapa'
  return {
    slaStates: isK1 ? [] : filters.slaStates,
    stages: isK2 ? [] : filters.stages,
    onlyOverdue: filters.onlyOverdue
  }
}

const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/** `now − 7 d`, no piso da hora: o recorte do Kanban não muda a cada segundo (mantém páginas). */
export function closedSinceFor(nowMs: number): string {
  const since = nowMs - DS_ATTENDANCE_CLOSED_WINDOW_DAYS * DAY_MS
  return new Date(Math.floor(since / HOUR_MS) * HOUR_MS).toISOString()
}

export function applyDsAttendanceQueueParams<T extends DsAttendanceQueueBaseParams>(
  base: T,
  filters: DsAttendanceQueueFilters,
  ctx: QueueParamsContext
): T {
  // Solicitações é recorte próprio (CA9): nada de SLA da fila entra lá.
  if (base.onlyRequests) return base
  const effective = effectiveQueueFilters(filters)
  const stage: ContainerStage[] = effective.stages.length
    ? [...effective.stages]
    : [...CONTAINER_STAGES]
  return {
    ...base,
    excludeRequests: true,
    stage,
    slaState: effective.slaStates.length ? [...effective.slaStates] : undefined,
    overdue: effective.onlyOverdue || undefined,
    closedSince:
      stage.includes('CLOSED') && !ctx.hasExplicitHistory ? closedSinceFor(ctx.nowMs) : undefined
  }
}

/**
 * Recorte SLA do "próximo da fila" a partir da URL da lista (B7). Sem Etapa nem Status
 * escolhidos, o "próximo" fica nas etapas ABERTAS (F7): "Aprovar e abrir o próximo" nunca abre
 * um encerrado. Com Status explícito (link antigo, modo histórico) o status vale como veio,
 * cruzado com as 4 etapas como na lista, sem o corte de 7 dias.
 */
export function containerQueueParamsFromQuery(
  query: Record<string, unknown>,
  nowMs: number = Date.now()
): Partial<ListContainersByCompanyParams> {
  if (first(query.onlyRequests) === 'true') return {}
  const filters = dsAttendanceQueueFiltersFromUrl(query, 'list')
  const hasExplicitHistory = Boolean(
    first(query.status) || first(query.startDate) || first(query.endDate)
  )
  const params: Partial<ListContainersByCompanyParams> = applyDsAttendanceQueueParams({}, filters, {
    nowMs,
    hasExplicitHistory
  })
  if (!effectiveQueueFilters(filters).stages.length && !hasExplicitStatus(query)) {
    params.stage = [...OPEN_CONTAINER_STAGES]
    params.closedSince = undefined
  }
  return params
}

const VALID_STATUSES = Object.values(DSAttendanceStatus) as string[]
const hasExplicitStatus = (query: Record<string, unknown>): boolean =>
  csv(query.status).some((status) => VALID_STATUSES.includes(status))

export function countActiveQueueFilters(filters: DsAttendanceQueueFilters): number {
  const effective = effectiveQueueFilters(filters)
  return effective.slaStates.length + effective.stages.length + (effective.onlyOverdue ? 1 : 0)
}

export function queueEmptyTitle(input: {
  onlyOverdue: boolean
  hasActiveFilters: boolean
}): string {
  if (input.onlyOverdue) return 'Nada vencido — tudo em dia'
  if (input.hasActiveFilters) return 'Nenhum resultado para estes filtros'
  return 'Nenhum registro encontrado'
}

const toggle = <T>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

export function useDsAttendanceQueueFilters() {
  const filters = ref<DsAttendanceQueueFilters>({
    ...DEFAULT_DS_ATTENDANCE_QUEUE_FILTERS,
    slaStates: [],
    stages: []
  })

  const showSlaStateFilter = computed(
    () => !(filters.value.view === 'kanban' && filters.value.groupBy === 'estado')
  )
  const showStageFilter = computed(
    () => !(filters.value.view === 'kanban' && filters.value.groupBy === 'etapa')
  )
  const activeCount = computed(() => countActiveQueueFilters(filters.value))

  /** URL > preferência (`queue-view:ds-attendance`) > Kanban. */
  function hydrate(query: Record<string, unknown>): void {
    filters.value = dsAttendanceQueueFiltersFromUrl(
      query,
      readQueueViewPreference(DS_ATTENDANCE_QUEUE_SCREEN_KEY, 'kanban')
    )
  }

  function update(patch: Partial<DsAttendanceQueueFilters>): void {
    filters.value = { ...filters.value, ...patch }
  }

  /** "Limpar filtros" não mexe em visão nem agrupamento (são navegação). */
  function clear(): void {
    filters.value = { ...filters.value, slaStates: [], stages: [], onlyOverdue: false }
  }

  return {
    filters,
    showSlaStateFilter,
    showStageFilter,
    activeCount,
    hydrate,
    update,
    clear,
    toggleSlaState: (state: ContainerSlaFilterState) =>
      update({ slaStates: toggle(filters.value.slaStates, state) }),
    toggleStage: (stage: ContainerStage) => update({ stages: toggle(filters.value.stages, stage) }),
    toUrl: () => dsAttendanceQueueFiltersToUrl(filters.value),
    toParams: <T extends DsAttendanceQueueBaseParams>(base: T, ctx: QueueParamsContext) =>
      applyDsAttendanceQueueParams(base, filters.value, ctx)
  }
}
